// features/item-filter/DrillDownChip.jsx
//
// The visible answer to "why does this chart show one line?". When a grid
// row is drilled into, the chart beside it narrows to that single
// item-location, and nothing else on screen says so. The chip carries the same primary
// accent as the drilled row's highlight (grid-page.css
// `.ag-row.*-row-active`), so the row and the chart scope read as one
// state, and it names the scope explicitly instead of only naming the item.
//
// Takes the `drillDown` value object and `clearDrillDown` straight from
// useGridChartScope — renders nothing when no row is drilled into, so
// pages mount it unconditionally.
//
// The item is NAMED through formatItemLabel, the app's one home for
// "how a merchant reads this item": product name (SKU) on image-capable
// stores, where the item code is a variant-GID suffix nobody recognizes,
// and "code — name (sku)" everywhere else. The chip is the display
// surface, so the store-capability branch belongs here rather than in the
// scope object the grids build.

import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { ICON, Target, X } from '../../icons';
import { useIsShopify } from '../../contexts/AuthContext';
import { formatItemLabel } from '../../utils/itemLabel';
import './DrillDownChip.css';

const DrillDownChip = ({ drillDown, onClear }) => {
  const { t } = useTranslation();
  const isShopify = useIsShopify();

  if (!drillDown) return null;

  const { itemCode, itemName, sku, locationName } = drillDown;
  const itemLabel = formatItemLabel(
    { item_code: itemCode, item_name: itemName, sku },
    isShopify,
  );

  return (
    <span className="drill-down-chip">
      <Target className="drill-down-chip-icon" size={ICON.sm} weight="bold" />
      <span className="drill-down-chip-label">{t('searchFilters.drillDownLabel')}</span>
      {/* LocationName is a required, non-blank field enforced on every save
          (backend/api/models.py Location.clean/save → full_clean), so it
          needs no stand-in — a blank one here means a data bug worth seeing. */}
      <span className="drill-down-chip-value">
        {`${itemLabel} — ${locationName}`}
      </span>
      <Tooltip title={t('searchFilters.drillDownClear')}>
        <button
          type="button"
          className="drill-down-chip-clear"
          aria-label={t('searchFilters.drillDownClear')}
          // The chip lives in a collapsible tile header; without this the
          // clear click also toggles the tile.
          onClick={(e) => { e.stopPropagation(); onClear(); }}
        >
          <X size={ICON.xs} weight="bold" />
        </button>
      </Tooltip>
    </span>
  );
};

export default DrillDownChip;
